"use client";

import React, { useEffect, useRef, useState, useMemo, useLayoutEffect } from "react";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { DRACOLoader } from "three/examples/jsm/loaders/DRACOLoader.js";
import { MeshoptDecoder } from 'three/examples/jsm/libs/meshopt_decoder.module.js';
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

// --- SHADERS (Updated for better visibility) ---
const vertexShader = `
  varying vec3 vPosition;
  void main() {
    vPosition = position;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const fragmentShaderWireframe = `
  uniform float uProgress;
  uniform vec3 uColor;
  varying vec3 vPosition;

  void main() {
    if (uProgress < 0.01) discard;
    float normalizedY = vPosition.y * 0.1;
    float scanLine = normalizedY + uProgress * 2.0;
    float scanPosition = fract(scanLine); 
    float scanGlow = 1.0 - smoothstep(0.0, 0.15, abs(scanPosition - uProgress));
    float visibility = smoothstep(0.0, 1.0, uProgress);
    // Fade out at the end of lifecycle
    if(uProgress > 2.2) visibility = smoothstep(2.5, 2.2, uProgress);

    // Reduced opacity for better text visibility
    vec3 finalColor = uColor + (vec3(1.2) * scanGlow);
    gl_FragColor = vec4(finalColor, visibility * 0.3); // Reduced from default to 0.3
  }
`;

// --- DATA (Updated colors to be less intense) ---
const MODELS = [
  { id: "phase1", path: "/server_final.glb", scale: 0.25, color: 0x0066aa }, // Darker blue
  { id: "phase2", path: "/cpu_final.glb",    scale: 0.3,  color: 0x0055bb }, // Darker blue
  { id: "phase3", path: "/GPU_mobile.glb",   scale: 2.0,  color: 0x0088cc }  // Darker cyan
];

const PHASES = [
  {
    id: "01",
    label: "DECLARE",
    sub: "INTENT SIGNALING",
    desc: "Execution intent is declared. Intelligence parameters are defined.",
    hex: "0x882F"
  },
  {
    id: "02",
    label: "ALLOCATE",
    sub: "COMPUTE MATERIALIZATION",
    desc: "Resources provisioned. Characteristics adapt dynamically.",
    hex: "0x4A1D"
  },
  {
    id: "03",
    label: "EXECUTE",
    sub: "DEPLOYING OUTPUT",
    desc: "Models evolve in real time. Output is produced continuously.",
    hex: "0x99E1"
  }
];

export default function PhaseFluidShowcase() {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  // We use this array to target the specific HTML elements for GSAP
  const phaseRefs = useRef<(HTMLDivElement | null)[]>([]);
  
  const [isMounted, setIsMounted] = useState(false);

  const backgroundData = useMemo(() => {
    return Array.from({ length: 60 }, (_, i) => {
      return ((i * 123456789) % 0xFFFFFFFF).toString(16).toUpperCase().padStart(8, '0').substring(0, 10);
    });
  }, []);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIsMounted(true);
  }, []);

  // --- THREE.JS LOGIC (Updated lighting and model rendering) ---
  useLayoutEffect(() => {
    if (!isMounted || !canvasRef.current || !containerRef.current) return;

    const sizes = { width: window.innerWidth, height: window.innerHeight };
    const scene = new THREE.Scene();
    
    const camera = new THREE.PerspectiveCamera(35, sizes.width / sizes.height, 0.1, 100);
    camera.position.set(0, 0, 10);

    const renderer = new THREE.WebGLRenderer({
      canvas: canvasRef.current,
      antialias: true,
      alpha: true,
      powerPreference: "high-performance"
    });
    renderer.setSize(sizes.width, sizes.height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

    // Reduced lighting intensity
    const ambient = new THREE.AmbientLight(0xffffff, 0.3);
    scene.add(ambient);
    const keyLight = new THREE.DirectionalLight(0xffffff, 1.5);
    keyLight.position.set(5, 5, 5);
    scene.add(keyLight);
    const rimLightA = new THREE.SpotLight(0x0066aa, 35); // Reduced intensity
    rimLightA.position.set(-5, 5, -5);
    scene.add(rimLightA);
    const rimLightB = new THREE.SpotLight(0x0044aa, 40); // Reduced intensity
    rimLightB.position.set(5, -2, -5);
    scene.add(rimLightB);

    const gltfLoader = new GLTFLoader();
    const dracoLoader = new DRACOLoader();
    dracoLoader.setDecoderPath("/draco/");
    gltfLoader.setDRACOLoader(dracoLoader);
    gltfLoader.setMeshoptDecoder(MeshoptDecoder);

    const modelGroups: THREE.Group[] = [];
    const progressProxy = { p1: 0, p2: 0, p3: 0 };
    scene.userData.proxy = progressProxy;

    // Load Models
    MODELS.forEach((config, index) => {
      gltfLoader.load(config.path, (gltf) => {
        const model = gltf.scene;
        const box = new THREE.Box3().setFromObject(model);
        const center = box.getCenter(new THREE.Vector3());
        model.position.sub(center);
        
        const wrapper = new THREE.Group();
        wrapper.scale.setScalar(config.scale);
        wrapper.rotation.set(0.2, -0.5, 0);
        
        model.traverse((child) => {
          if ((child as THREE.Mesh).isMesh) {
            const mesh = child as THREE.Mesh;
            mesh.renderOrder = index + 1;

            const wireframeMat = new THREE.ShaderMaterial({
              vertexShader,
              fragmentShader: fragmentShaderWireframe,
              uniforms: {
                  uProgress: { value: 0 },
                  uColor: { value: new THREE.Color(config.color) }
              },
              transparent: true,
              depthWrite: false,
              blending: THREE.AdditiveBlending,
            });
            
            const edges = new THREE.EdgesGeometry(mesh.geometry, 25);
            const lineSegments = new THREE.LineSegments(edges, wireframeMat);
            mesh.add(lineSegments);
            mesh.material = new THREE.MeshBasicMaterial({ visible: false });
          }
        });

        wrapper.add(model);
        wrapper.visible = false; // Hidden initially
        scene.add(wrapper);
        modelGroups[index] = wrapper;
      });
    });

    // --- RENDER LOOP ---
    const clock = new THREE.Clock();
    let rafId: number;

    const animate = () => {
      const time = clock.getElapsedTime();
      
      // Reduced light pulse intensity
      rimLightA.intensity = 35 + Math.cos(time * 1.8) * 8;
      rimLightB.intensity = 40 + Math.sin(time * 2) * 12;

      modelGroups.forEach((group, i) => {
        if (!group) return;

        // Get the progress from the proxy (updated by GSAP)
        let pVal = 0;
        if (i === 0) pVal = progressProxy.p1;
        if (i === 1) pVal = progressProxy.p2;
        if (i === 2) pVal = progressProxy.p3;

        // Visibility Logic
        group.visible = pVal > 0.001 && pVal < 2.45;

        if (group.visible) {
            // Apply uniforms
            group.traverse((child) => {
               if ((child as THREE.Mesh).isMesh) {
                  const mesh = child as THREE.Mesh;
                  mesh.children.forEach((c) => {
                      const mat = (c as THREE.Mesh).material as THREE.ShaderMaterial | undefined;
                      if (mat?.uniforms) mat.uniforms.uProgress.value = pVal;
                  });
               }
            });
            // Float animation
            group.position.y = Math.sin(time * 0.5 + i) * 0.15;
            group.rotation.y = -0.5 + (Math.sin(time * 0.2) * 0.1);
        }
      });

      renderer.render(scene, camera);
      rafId = requestAnimationFrame(animate);
    };
    animate();

    const handleResize = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };
    window.addEventListener("resize", handleResize);

    // --- GSAP ANIMATION ---
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: containerRef.current,
          start: "top top",
          end: "+=3500", // Adjusted scroll length
          scrub: 1,
          pin: true,
          anticipatePin: 1,
        },
      });

      // Animate Phases
      PHASES.forEach((_, i) => {
         const el = phaseRefs.current[i];
         if(!el) return;

         // Select inner elements
         const fillText = el.querySelector(".cestus-fill");
         const bar = el.querySelector(".cestus-bar");
         const details = el.querySelector(".cestus-details");
         const glitch = el.querySelectorAll(".cestus-glitch");

         const start = i * 0.33;
         const end = start + 0.33;

         // 1. Reveal Container
         // First phase starts visible, others fade in
         if (i === 0) {
            tl.set(el, { opacity: 1 }, 0);
         } else {
            tl.fromTo(el, { opacity: 0, scale: 0.95 }, { opacity: 1, scale: 1, duration: 0.05 }, start);
         }

         // 2. The "Cestus" Text Wipe
         tl.fromTo(fillText, 
            { clipPath: "inset(0 100% 0 0)" }, 
            { clipPath: "inset(0 0% 0 0)", duration: 0.2, ease: "power2.out" }, 
            start
         );

         // 3. Glitch Flash
         tl.fromTo(glitch, { opacity: 0 }, { opacity: 0.5, duration: 0.05, yoyo: true, repeat: 3 }, start);

         // 4. Bar Growth
         tl.fromTo(bar, { scaleX: 0 }, { scaleX: 1, duration: 0.2, ease: "power2.out" }, start);

         // 5. Details Fade In
         tl.fromTo(details, { opacity: 0, y: 10 }, { opacity: 1, y: 0, duration: 0.1 }, start + 0.1);

         // 6. Update 3D Model Proxy
         const pKey = i === 0 ? 'p1' : i === 1 ? 'p2' : 'p3';
         tl.to(progressProxy, { [pKey]: 2.5, duration: 0.33, ease: "none" }, start);

         // 7. Exit (Blur out) - except last one stays a bit
         if(i < 2) {
            tl.to(el, { opacity: 0, filter: "blur(10px)", scale: 1.1, duration: 0.05 }, end - 0.05);
         }
      });

    }, containerRef);

    return () => {
      window.removeEventListener("resize", handleResize);
      ctx.revert();
      cancelAnimationFrame(rafId);
      renderer.dispose();
      scene.clear();
    };
  }, [isMounted]);

  if (!isMounted) return <div className="min-h-screen bg-[#010206]" />;

  return (
    <div ref={containerRef} className="relative w-full h-screen bg-[#010206]">
      
      {/* 3D CANVAS */}
      <canvas ref={canvasRef} className="block w-full h-full z-10 relative" />

      {/* KINETIC BACKGROUND */}
      <div className="absolute inset-0 z-0 opacity-[0.06] select-none pointer-events-none font-mono text-[10px] flex flex-wrap gap-x-4 gap-y-2 p-4 overflow-hidden">
        {backgroundData.map((hex, i) => (
          <span key={i} className="whitespace-nowrap text-blue-500">
            0x{hex}
          </span>
        ))}
      </div>

      {/* ATMOSPHERE */}
      <div className="absolute inset-0 pointer-events-none z-20 bg-[radial-gradient(circle_at_center,transparent_20%,#010206_100%)]" />
      <div className="absolute inset-0 pointer-events-none z-20 bg-[url('/noise.svg')] opacity-10 mix-blend-overlay"></div>

      {/* UI OVERLAYS - Enhanced contrast */}
      <div className="absolute inset-0 z-30 pointer-events-none flex items-center justify-center">
        {PHASES.map((phase, i) => (
            <div 
                key={phase.id}
                ref={(el) => { phaseRefs.current[i] = el; }}
                className={`absolute flex flex-col items-center justify-center w-full max-w-5xl ${i === 0 ? 'opacity-100' : 'opacity-0'}`}
            >
                {/* 1. LAYERED TITLE (Cestus Style) - Enhanced visibility */}
                <div className="relative text-center mb-8">
                    
                    {/* Layer 1: Ghost Stroke (Always visible guide) */}
                    <h1 
                        className="text-[14vw] md:text-[11vw] font-black leading-none tracking-tighter text-transparent uppercase select-none"
                        style={{ WebkitTextStroke: '2px rgba(255,255,255,0.15)' }}
                    >
                        {phase.label}
                    </h1>

                    {/* Layer 2: Fill (Animated via Clip-Path) - Added text shadow for better contrast */}
                    <h1 
                        className="cestus-fill absolute top-0 left-0 w-full text-[14vw] md:text-[11vw] font-black leading-none tracking-tighter text-white uppercase select-none z-10"
                        style={{ 
                          clipPath: 'inset(0 100% 0 0)',
                          textShadow: '0 0 40px rgba(0,0,0,0.8), 0 0 20px rgba(0,0,0,0.9)'
                        }} 
                    >
                        {phase.label}
                    </h1>

                    {/* Layer 3: Glitch Effects (Animated Opacity) */}
                    <h1 className="cestus-glitch absolute top-0 left-1 w-full text-[14vw] md:text-[11vw] font-black leading-none tracking-tighter text-red-500/75 uppercase mix-blend-screen opacity-0 select-none">
                        {phase.label}
                    </h1>
                    <h1 className="cestus-glitch absolute top-0 -left-1 w-full text-[14vw] md:text-[11vw] font-black leading-none tracking-tighter text-cyan-500/75 uppercase mix-blend-screen opacity-0 select-none">
                        {phase.label}
                    </h1>
                </div>

                {/* 2. SUB-INTERFACE (Cestus Style) - Enhanced background */}
                <div className="cestus-details opacity-0 flex flex-col items-center gap-4 w-full max-w-lg px-4">
                    
                    {/* Semi-transparent background for better text visibility */}
                    <div className="absolute inset-0 bg-gradient-to-b from-[#010206]/80 via-[#010206]/90 to-[#010206]/80 backdrop-blur-sm rounded-lg -z-10" />
                    
                    {/* Header Info */}
                    <div className="flex justify-between w-full font-mono text-[10px] tracking-[0.3em] text-blue-300">
                        <span className="flex items-center gap-2">
                          <span className="w-1.5 h-1.5 bg-blue-400 rounded-full animate-ping" />
                          SYS_PHASE_{phase.id}
                        </span>
                        <span className="text-white/90 animate-pulse">ACTIVE</span>
                    </div>

                    {/* Loading Bar */}
                    <div className="relative h-[2px] w-full bg-white/10">
                        <div 
                            className="cestus-bar absolute top-0 left-0 h-full w-full origin-left scale-x-0 bg-gradient-to-r from-blue-500 via-blue-300 to-white shadow-[0_0_30px_rgba(59,130,246,1)]"
                        />
                    </div>

                    {/* Description - Enhanced contrast */}
                    <div className="flex flex-col items-center gap-2 text-center mt-2">
                        <h3 className="font-mono text-blue-100 text-xs tracking-[0.25em] uppercase drop-shadow-lg">
                             {phase.sub}
                        </h3>
                        <p className="text-white/85 text-xs md:text-sm leading-relaxed font-mono max-w-sm drop-shadow-lg">
                             {phase.desc}
                        </p>
                    </div>

                    {/* Footer Data */}
                    <div className="flex justify-between w-full max-w-[320px] mt-4 font-mono text-[10px] text-white/75 uppercase tracking-widest border-t border-white/10 pt-2">
                        <span>Mem_Alloc: {phase.hex}</span>
                        <span>Substrate: Locked</span>
                        <span>Auth: Secured</span>
                    </div>
                </div>
            </div>
        ))}
      </div>

      {/* GLOBAL HUD */}
      <div className="absolute top-8 left-8 text-[10px] font-mono text-blue-400/75 tracking-widest hidden md:block z-40 pointer-events-none">
          [ CESTUS_PROTOCOL_V2 ]<br/>
          [ 127.0.0.1 ]
      </div>
    </div>
  );
}
